/**
 * Cache Management Utility for BeraStays
 * Handles cache clearing, versioning, and synchronization across devices
 */

const CACHE_VERSION_KEY = "berastays_cache_version";
const CURRENT_CACHE_VERSION = "1.0.1"; // Incremented: migration from hidden_bookings to permanently_deleted
const HIDDEN_BOOKINGS_KEY = "berastays_hidden_bookings"; // DEPRECATED: use PERMANENTLY_DELETED_KEY
const PERMANENTLY_DELETED_KEY = "bera_permanently_deleted";

/**
 * Check if cache needs to be cleared (app version changed)
 */
export function checkAndClearCache(): void {
  const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);

  if (storedVersion !== CURRENT_CACHE_VERSION) {
    console.log(`[CacheManager] Version changed from ${storedVersion} to ${CURRENT_CACHE_VERSION}. Clearing caches...`);

    // MIGRATE: Move hidden_bookings to permanently_deleted before clearing
    migrateHiddenBookingsToPermanentlyDeleted();

    // Clear localStorage except for critical data
    clearLocalStorage();

    // Clear sessionStorage
    sessionStorage.clear();

    // Store new version
    localStorage.setItem(CACHE_VERSION_KEY, CURRENT_CACHE_VERSION);

    // Reload to ensure clean state
    if (storedVersion) {
      window.location.reload();
    }
  }
}

/**
 * MIGRATION: Move data from deprecated hidden_bookings to permanently_deleted
 * This ensures deleted bookings stay deleted after cache clear
 */
function migrateHiddenBookingsToPermanentlyDeleted(): void {
  try {
    const hiddenRaw = localStorage.getItem(HIDDEN_BOOKINGS_KEY);
    if (!hiddenRaw) return;

    const hidden: Record<string, { timestamp: number; expiresAt: number }> = JSON.parse(hiddenRaw);
    const hiddenIds = Object.keys(hidden);

    if (hiddenIds.length === 0) return;

    // Get current permanently deleted list
    const existing = JSON.parse(localStorage.getItem(PERMANENTLY_DELETED_KEY) || '[]');
    const existingSet = new Set(existing);

    // Add all hidden bookings to permanently deleted
    let addedCount = 0;
    hiddenIds.forEach((id) => {
      if (!existingSet.has(id)) {
        existing.push(id);
        addedCount++;
      }
    });

    if (addedCount > 0) {
      localStorage.setItem(PERMANENTLY_DELETED_KEY, JSON.stringify(existing));
      console.log(`[CacheManager] MIGRATED ${addedCount} hidden bookings to permanently deleted`);
    }

    // Remove deprecated key
    localStorage.removeItem(HIDDEN_BOOKINGS_KEY);
    console.log('[CacheManager] Removed deprecated hidden_bookings key');
  } catch (err) {
    console.error('[CacheManager] Migration failed:', err);
  }
}

/**
 * Clear localStorage selectively - preserve auth and critical data
 */
export function clearLocalStorage(): void {
  const keysToPreserve = [
    "supabase.auth.token", // Preserve auth session
    "berastays_cache_version", // Preserve version
    PERMANENTLY_DELETED_KEY, // CRITICAL: Preserve deleted items list
  ];
  
  const preserved: Record<string, string> = {};
  
  // Save preserved keys
  keysToPreserve.forEach((key) => {
    const value = localStorage.getItem(key);
    if (value) preserved[key] = value;
  });
  
  // Clear all
  localStorage.clear();
  
  // Restore preserved keys
  Object.entries(preserved).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });
}

/**
 * Add hidden booking ID(s) with expiration (30 days)
 */
export function addHiddenBookingId(bookingId: string): void;
export function addHiddenBookingId(bookingIds: string[]): void;
export function addHiddenBookingId(ids: string | string[]): void {
  try {
    const raw = localStorage.getItem(HIDDEN_BOOKINGS_KEY);
    const hidden = raw ? JSON.parse(raw) : {};
    const now = Date.now();
    const expiresAt = now + 1000 * 60 * 60 * 24 * 30; // 30 days
    
    const idArray = Array.isArray(ids) ? ids : [ids];
    
    idArray.forEach((id) => {
      hidden[id] = {
        timestamp: now,
        expiresAt: expiresAt,
      };
    });
    
    localStorage.setItem(HIDDEN_BOOKINGS_KEY, JSON.stringify(hidden));
  } catch {
    console.error("[CacheManager] Failed to add hidden booking(s)");
  }
}

// Alias for backward compatibility
export const addHiddenBookingIds = addHiddenBookingId;

/**
 * Get valid hidden booking IDs (filter expired)
 */
export function getHiddenBookingIds(): string[] {
  try {
    const raw = localStorage.getItem(HIDDEN_BOOKINGS_KEY);
    if (!raw) return [];
    
    const hidden: Record<string, { timestamp: number; expiresAt: number }> = JSON.parse(raw);
    const now = Date.now();
    const valid: string[] = [];
    const expired: string[] = [];
    
    Object.entries(hidden).forEach(([id, data]) => {
      if (data.expiresAt > now) {
        valid.push(id);
      } else {
        expired.push(id);
      }
    });
    
    // Clean up expired entries
    if (expired.length > 0) {
      expired.forEach((id) => delete hidden[id]);
      localStorage.setItem(HIDDEN_BOOKINGS_KEY, JSON.stringify(hidden));
    }
    
    return valid;
  } catch {
    return [];
  }
}

/**
 * Clear all hidden bookings for current user
 */
export function clearHiddenBookings(): void {
  localStorage.removeItem(HIDDEN_BOOKINGS_KEY);
}

/**
 * Check if specific booking is hidden
 */
export function isBookingHidden(bookingId: string): boolean {
  return getHiddenBookingIds().includes(bookingId);
}

/**
 * Clear all application caches
 */
export async function clearAllCaches(): Promise<void> {
  // Clear localStorage
  clearLocalStorage();
  
  // Clear sessionStorage
  sessionStorage.clear();
  
  // Clear service worker caches if present
  if ("caches" in window) {
    try {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    } catch (e) {
      console.error("[CacheManager] Failed to clear service worker caches", e);
    }
  }
  
  // Reload page
  window.location.reload();
}

/**
 * Force refresh all data by clearing React Query cache
 */
export function forceRefresh(queryClient: any): void {
  queryClient.clear();
  queryClient.invalidateQueries({ queryKey: ["my-bookings"] });
  queryClient.invalidateQueries({ queryKey: ["profile"] });
  queryClient.invalidateQueries({ queryKey: ["notifications"] });
  queryClient.invalidateQueries({ queryKey: ["properties"] });
}

/**
 * Add booking ID to permanently deleted list
 * This prevents the booking from EVER reappearing in the UI
 */
export function addPermanentlyDeleted(bookingId: string): void {
  try {
    const existing = JSON.parse(localStorage.getItem(PERMANENTLY_DELETED_KEY) || '[]');
    if (!existing.includes(bookingId)) {
      existing.push(bookingId);
      localStorage.setItem(PERMANENTLY_DELETED_KEY, JSON.stringify(existing));
    }
  } catch {
    console.error("[CacheManager] Failed to add permanently deleted booking");
  }
}

/**
 * Check if a booking is in the permanently deleted list
 */
export function isPermanentlyDeleted(bookingId: string): boolean {
  try {
    const deleted = JSON.parse(localStorage.getItem(PERMANENTLY_DELETED_KEY) || '[]');
    return deleted.includes(bookingId);
  } catch {
    return false;
  }
}

/**
 * Get all permanently deleted booking IDs
 */
export function getPermanentlyDeleted(): string[] {
  try {
    return JSON.parse(localStorage.getItem(PERMANENTLY_DELETED_KEY) || '[]');
  } catch {
    return [];
  }
}

/**
 * Clear permanently deleted list (use with caution)
 */
export function clearPermanentlyDeleted(): void {
  localStorage.removeItem(PERMANENTLY_DELETED_KEY);
}
